/**
 * `/studio/logo` — the Logo Design service page.
 *
 * For the visitor who wants the whole offer before tapping a card: the style
 * gallery, every rule, and the questions logo clients ask. The words come
 * from @codasc/studio-shared (LOGO_PAGE, LOGO_RULES, the FAQ lists), the same
 * source step 2 and the page's HTML are built from, so the rules a client
 * reads here are the ones they ticked on the form.
 *
 * Every way out is the form with the card preselected (`?type=logo`), which is
 * also where logo ads land. Plan: docs/studio/logo-service-dev-plan.md §4.
 */
import {
  CATEGORIES,
  LOGO_FAQ,
  LOGO_PAGE,
  LOGO_RULES,
  LOGO_STYLES,
  PAYMENT_FAQ,
  formatPesos,
} from '@codasc/studio-shared';
import { LOGO_STYLE_ICONS } from './logoStyles';
import { LEGAL, MESSENGER_URL } from './steps';

const QUOTE_URL = `${import.meta.env.BASE_URL}?type=logo`;
const FROM = CATEGORIES.find((c) => c.id === 'logo')?.fromCentavos ?? null;

function Actions({ onMessenger }: { onMessenger: () => void }) {
  return (
    <div className="st-lp-actions">
      <a className="st-btn st-btn-primary" href={QUOTE_URL}>
        Get a quote
      </a>
      <a
        className="st-btn st-btn-ghost"
        href={MESSENGER_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onMessenger}
      >
        Message us
      </a>
    </div>
  );
}

export function LogoPage({ onMessenger }: { onMessenger: () => void }) {
  return (
    <>
      <section className="st-hero" aria-labelledby="st-h1">
        <p className="st-eyebrow">Codasc Studio · Logo Design</p>
        <h1 id="st-h1">{LOGO_PAGE.h1}</h1>
        <p className="st-dek">{LOGO_PAGE.dek}</p>
        {FROM !== null ? <p className="st-lp-price">Starts at {formatPesos(FROM)}</p> : null}
        <Actions onMessenger={onMessenger} />
      </section>

      <div className="st-lp">
        <section aria-labelledby="st-lp-styles">
          <p className="st-eyebrow">What we design</p>
          <h2 id="st-lp-styles" className="st-sec-h">
            Logos like these.
          </h2>
          <ul className="st-lp-gallery">
            {LOGO_STYLES.filter((s) => LOGO_STYLE_ICONS[s.id]).map((s) => (
              <li key={s.id}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  {LOGO_STYLE_ICONS[s.id]}
                </svg>
                <span>{s.label}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="st-lp-rules">
          <p className="st-eyebrow">The rules</p>
          <h2 id="st-lp-rules" className="st-sec-h">
            How logo work goes.
          </h2>
          <div className="st-lp-rules">
            {LOGO_RULES.map(([title, points]) => (
              <div key={title}>
                <h3>{title}</h3>
                <ul>
                  {points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="st-fine">
            The full terms are under Studio Services in our{' '}
            <a href={`${LEGAL}/terms#studio-logo`} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            .
          </p>
        </section>

        <section aria-labelledby="st-lp-faq">
          <p className="st-eyebrow">Questions</p>
          <h2 id="st-lp-faq" className="st-sec-h">
            Before you ask.
          </h2>
          <dl className="st-faq">
            {[...LOGO_FAQ, ...PAYMENT_FAQ].map(([q, a]) => (
              <div key={q}>
                <dt>{q}</dt>
                <dd>{a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="st-lp-end" aria-labelledby="st-lp-end">
          <h2 id="st-lp-end" className="st-sec-h">
            Tell us about your business.
          </h2>
          <p className="st-dek">A few taps and a sentence or two. Free to ask, no commitment.</p>
          <Actions onMessenger={onMessenger} />
        </section>
      </div>
    </>
  );
}
